import { Button } from '@1trading/react/components/ui/button';
import { Loading } from '@1trading/react/components/ui/loading';
import { Separator } from '@1trading/react/components/ui/separator';
import { useToast } from '@1trading/react/hooks/use-toast';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { GuardedFormStepper, useGuardedFormStepper } from '@/components/ui/guarded-form-stepper';
import LoadingCard from '@/components/ui/loading-card';
import { PageCard, PageCardContent, PageCardFooter } from '@/components/ui/page-card';
import { PageHeading, PageHeadingBackButton, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import { InstitutionOnboardingProgressStep, useProgress } from '@/context/progress';
import { type InstitutionComplianceTraining, institutionComplianceTrainingToInstitutionDetailsComplianceTrainingApi } from '@/features/institution/compliance-training/schema';
import { InstitutionGoToReviewStepperButton } from '@/features/institution/components/go-to-review-button';
import { useInstitutionOnboardingBasePath } from '@/features/institution/context/update';
import { useDetailsFlowControl } from '@/features/institution/hooks/useDetailsFlowControl';
import { useInstitutionDetailsMutation, useInstitutionDetailsV2Answers, useInstitutionDetailsVersion } from '@/features/institution/hooks/useInstitutionDetails';
import { getDetailsKeyProgress, INSTITUTION_DETAILS_TYPE } from '@/features/institution/utils';
import { institutionDetailsV2ComplianceTrainingSchema } from '@/services/api/profile';
import type { FormStep } from '@/types/components';
import { InstitutionComplianceTrainingFieldsetProgramme } from './fieldsets/programme';
import { InstitutionComplianceTrainingFieldsetTraining } from './fieldsets/training';

const InstitutionComplianceTrainingFormStepTrainingAndPoliciesLegend = () => {
  const { t } = useTranslation();

  return (
    <PageHeading asChild>
      <legend>
        <PageHeadingTitle>{t('institutionDetails.complianceTraining.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('institutionDetails.complianceTraining.description')}</PageHeadingDescription>
      </legend>
    </PageHeading>
  );
};
InstitutionComplianceTrainingFormStepTrainingAndPoliciesLegend.displayName = 'InstitutionComplianceTrainingFormStepTrainingAndPoliciesLegend';

const InstitutionComplianceTrainingFormStepTrainingAndPolicies = () => (
  <div className="space-y-6">
    <InstitutionComplianceTrainingFieldsetTraining />
    <Separator />
    <InstitutionComplianceTrainingFieldsetProgramme />
  </div>
);
InstitutionComplianceTrainingFormStepTrainingAndPolicies.displayName = 'InstitutionComplianceTrainingFormStepTrainingAndPolicies';

const FORM_STEPS = {
  TRAINING_AND_POLICIES: {
    Component: InstitutionComplianceTrainingFormStepTrainingAndPolicies,
    Legend: InstitutionComplianceTrainingFormStepTrainingAndPoliciesLegend,
    schema: institutionDetailsV2ComplianceTrainingSchema,
  },
} satisfies Record<string, FormStep<InstitutionComplianceTraining>>;

const STEPS = Object.values(FORM_STEPS).map((step) => step.Component);
const STEP_LEGENDS = Object.values(FORM_STEPS).map((step) => step.Legend);
const STEP_SCHEMAS = Object.values(FORM_STEPS).map((step) => step.schema);

const InstitutionComplianceTrainingFormCurrentStep = () => {
  const { currentStep, totalSteps } = useGuardedFormStepper<InstitutionComplianceTraining>();
  const { data: version } = useInstitutionDetailsVersion();
  const { setCurrentStep } = useProgress();
  const Step = STEPS[currentStep];

  useEffect(() => {
    if (!version) return;

    return setCurrentStep(
      InstitutionOnboardingProgressStep.INSTITUTION_INFORMATION,
      getDetailsKeyProgress(INSTITUTION_DETAILS_TYPE.ONBOARDING, version, 'complianceTraining', currentStep, totalSteps),
    );
  }, [currentStep, setCurrentStep, totalSteps, version]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: We want to scroll when currentStep changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  if (!Step) throw new Error('Step not found');

  return (
    <PageCardContent className="pt-8">
      <div className="animate-in fade-in" key={currentStep}>
        <Step />
      </div>
    </PageCardContent>
  );
};
InstitutionComplianceTrainingFormCurrentStep.displayName = 'InstitutionComplianceTrainingFormCurrentStep';

const InstitutionComplianceTrainingFormCurrentStepLegend = () => {
  const { currentStep } = useGuardedFormStepper<InstitutionComplianceTraining>();
  const Legend = STEP_LEGENDS[currentStep];

  if (!Legend) throw new Error('Legend not found');

  return <Legend />;
};
InstitutionComplianceTrainingFormCurrentStepLegend.displayName = 'InstitutionComplianceTrainingFormCurrentStepLegend';

const InstitutionComplianceTrainingFormBackButton = () => {
  const { currentStep, prevStep } = useGuardedFormStepper<InstitutionComplianceTraining>();
  const navigate = useNavigate();
  const onboardingPath = useInstitutionOnboardingBasePath();

  const handlePrevStep = useCallback(() => {
    if (currentStep === 0) {
      navigate(`${onboardingPath}/aml-ctf-sanctions`, { replace: true });
    } else {
      prevStep();
    }
  }, [currentStep, prevStep, navigate, onboardingPath]);

  return <PageHeadingBackButton floatingInPage="left" fixedInLayout onClick={handlePrevStep} />;
};
InstitutionComplianceTrainingFormBackButton.displayName = 'InstitutionComplianceTrainingFormBackButton';

const InstitutionComplianceTrainingFormFooter = ({ isPending }: { isPending: boolean }) => {
  const { currentStep, nextStep } = useGuardedFormStepper<InstitutionComplianceTraining>();
  const { t } = useTranslation();
  const Step = STEPS[currentStep];

  if (Step && 'isAutoNextStep' in Step && Step.isAutoNextStep) return null;

  return (
    <PageCardFooter>
      <Button className="w-full relative" size="lg" variant="primary" disabled={isPending} onClick={() => nextStep()}>
        <span className={isPending ? 'invisible' : ''}>{t('buttons.continue')}</span>
        {isPending ? <Loading variant="inverted" position="overlay" size="sm" /> : null}
      </Button>
    </PageCardFooter>
  );
};
InstitutionComplianceTrainingFormFooter.displayName = 'InstitutionComplianceTrainingFormFooter';

const InstitutionComplianceTrainingForm = () => {
  const { isLoading: isLoadingInstitutionDetailsAnswers, data: institutionDetailsAnswers } = useInstitutionDetailsV2Answers();
  const { isLoading: isLoadingInstitutionDetailsFlow } = useDetailsFlowControl(INSTITUTION_DETAILS_TYPE.ONBOARDING, 'complianceTraining');
  const { isPending, mutateAsync } = useInstitutionDetailsMutation();
  const navigate = useNavigate();
  const onboardingPath = useInstitutionOnboardingBasePath();
  const { toast } = useToast();
  const { t } = useTranslation();
  const isLoading = isLoadingInstitutionDetailsAnswers || isLoadingInstitutionDetailsFlow;

  const onSubmit = async (data: InstitutionComplianceTraining, { isReturningToReview = false }: { isReturningToReview?: boolean } = {}) => {
    await mutateAsync(
      { complianceTraining: await institutionComplianceTrainingToInstitutionDetailsComplianceTrainingApi(data) },
      {
        onError: () => {
          toast({
            description: t('fundamentals.defaultError.message'),
            variant: 'destructive',
          });
        },
        onSuccess: () => {
          navigate(isReturningToReview ? `${onboardingPath}/review` : `${onboardingPath}/kyc-cdd`, { replace: true });
        },
      },
    );
  };

  if (isLoading) return <LoadingCard />;

  const defaultValues = institutionDetailsAnswers?.complianceTraining ?? {};

  return (
    <GuardedFormStepper className="relative" defaultValues={defaultValues} steps={STEPS} stepSchemas={STEP_SCHEMAS} onSubmit={onSubmit}>
      <fieldset>
        <InstitutionComplianceTrainingFormCurrentStepLegend />
        <div className="flex flex-row justify-between -md:justify-end">
          <InstitutionComplianceTrainingFormBackButton />
          <InstitutionGoToReviewStepperButton onSubmit={(data: InstitutionComplianceTraining) => onSubmit(data, { isReturningToReview: true })} />
        </div>
        <PageCard size="lg" bordered fixedFooter>
          <InstitutionComplianceTrainingFormCurrentStep />
          <InstitutionComplianceTrainingFormFooter isPending={isPending} />
        </PageCard>
      </fieldset>
    </GuardedFormStepper>
  );
};
InstitutionComplianceTrainingForm.displayName = 'InstitutionComplianceTrainingForm';

export default InstitutionComplianceTrainingForm;
